import React from "react";
import {
  makeStyles,
  useTheme,
} from "@civicplus/preamble-ui/lib/Utilities/ThemeHelper";
import Grid from "@civicplus/preamble-ui/lib/Grid";
import { EventMeeting } from "../../types/types.event";
import EventVideoButton from "./EventVideoButton";
import EventDownloadFilesMenu from "./EventDownloadFilesMenu";
import SkeletonLoader from "@civicplus/preamble-ui/lib/SkeletonLoader";
import Button from "@civicplus/preamble-ui/lib/Button";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import { MeetingFile } from "../../components/EventDetails/MeetingFiles/interfaces";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";

const useStyles = makeStyles(theme => ({
  emptyButton: {
    width: theme.spacing(6),
    "@container eventListContainer (width < 400px)": {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
  },
  button: {
    "@container eventListContainer (width < 400px)": {
      padding: theme.spacing(0.5),
    },
  },
  loading: {
    marginLeft: theme.spacing(0.25),
  },
  topSpacing: {
    marginTop: "0.3rem",
  },
}));

interface EventListRowSecondaryActionsProps extends Partial<EventMeeting> {
  loading?: boolean;
  id?: EventMeeting["id"];
}

export const EventListRowSecondaryActions: React.FC<
  EventListRowSecondaryActionsProps
> = props => {
  const {
    id,
    hasMedia,
    externalMediaUrl,
    publishedFiles,
    liveIsCurrentlyStreaming,
    loading,
    publicCommentsEnabled,
    cpMediaOnly,
  } = props;
  const classes = useStyles(props);
  const theme = useTheme();
  const isSearchPage = window.location.pathname.toLowerCase() === "/search";
  const portalCustomization = useSelector(
    (state: RootState) => state.portalCustomization
  );
  const shouldRender =
    hasMedia ||
    liveIsCurrentlyStreaming ||
    loading ||
    (publishedFiles && publishedFiles?.length > 0);

  const handlePublishedFiles = (publishedFiles: MeetingFile[]) => {
    const publishedFileList:
      | MeetingFile[]
      | {
          fileId: number;
          name: string;
          type: string;
          url: string | undefined;
          fileType: number;
          plainText?: boolean;
        }[] = [];
    publishedFiles.forEach(attachment => {
      publishedFileList.push({
        fileId: attachment.fileId,
        name: attachment.name,
        type: `${attachment.type} (PDF)`,
        url: attachment.url,
        fileType: attachment.fileType,
      });
      publishedFileList.push({
        fileId: attachment.fileId,
        name: attachment.name,
        type: `${attachment.type} (Plain Text)`,
        url: attachment.url,
        plainText: true,
        fileType: attachment.fileType,
      });
    });
    return (
      <Grid data-testid="files">
        <EventDownloadFilesMenu
          id={`downloadFilesMenu-${id}`}
          publishedFiles={publishedFileList}
          title="Download Files from this Event"
          cpMediaOnly={cpMediaOnly || false}
          className={classes.button}
          iconSize="medium"
        />
      </Grid>
    );
  };

  return shouldRender ? (
    <>
      {loading ? (
        <Grid>
          <SkeletonLoader
            circle={true}
            height={theme.spacing(5)}
            width={theme.spacing(5)}
          />
        </Grid>
      ) : (
        (liveIsCurrentlyStreaming || loading || hasMedia || externalMediaUrl) &&
        id && (
          <Grid data-testid="video">
            <EventVideoButton
              externalMediaUrl={externalMediaUrl}
              eventId={id}
              isLive={liveIsCurrentlyStreaming}
              className={classes.button}
            />
          </Grid>
        )
      )}
      {loading ? (
        <Grid>
          <SkeletonLoader
            circle={true}
            height={theme.spacing(5)}
            width={theme.spacing(5)}
          />
        </Grid>
      ) : (
        !isSearchPage &&
        portalCustomization?.enablePublicSpeakerSignup &&
        publicCommentsEnabled &&
        publishedFiles &&
        publishedFiles.some(
          file => file.type === "Agenda Packet" || file.type === "Agenda"
        ) && (
          <Grid className={classes.button} data-testid="publicComments">
            <Button
              type="icon"
              color="primary"
              className={classes.button}
              title="Go To Public Comment"
              href={`/event/${id}/overview`}
              data-testid="Public Comment"
            >
              <RecordVoiceOverIcon />
            </Button>
          </Grid>
        )
      )}

      {loading ? (
        <Grid>
          <SkeletonLoader
            circle={true}
            height={theme.spacing(5)}
            width={theme.spacing(5)}
            className={classes.loading}
          />
        </Grid>
      ) : !isSearchPage &&
        publishedFiles &&
        publishedFiles?.length > 0 &&
        id ? (
        handlePublishedFiles(publishedFiles)
      ) : (
        <Grid className={classes.emptyButton}></Grid>
      )}
    </>
  ) : (
    <></>
  );
};

export default EventListRowSecondaryActions;
